/**
 * @file        src/lib/leadMessage.ts
 * @description Composes the single `message` string a lead carries to
 *              /api/lead, Telegram and site-leads.log out of the optional
 *              fields the newer form variants collect (body, origin, budget,
 *              wishes, messenger and account), the visitor's free comment and the
 *              A/B variant label.
 * @runs        Server and client (pure function, no I/O, no DOM).
 * @universal   composeLeadMessage — the ONE place that decides how extra
 *              lead fields become text. Registered in
 *              knowledge/universals/tools.md. Any future form field is added
 *              HERE, not by concatenating strings at a call site.
 * @rule        THE API CONTRACT IS FROZEN (series home-stage-b, invariant
 *              B-I5). /api/lead still receives phone/name/message/source/
 *              subject and nothing else; every new field the form collects
 *              travels inside `message`. This keeps the server, the bot
 *              formatter and the leads log untouched by the A/B test.
 * @rule        EMPTY MEANS ABSENT. A field the visitor did not fill is left
 *              out of the string entirely — never «Бюджет: —», never an
 *              empty label. With nothing to say the function returns
 *              `undefined`, exactly what the form sent before this series,
 *              so variant A keeps its byte-identical payload.
 * @rule        HARD CEILING 1000 CHARACTERS. The server truncates at 1000;
 *              a message that arrives already cut in the middle of a field
 *              is unreadable for the manager. The client caps the two free
 *              fields (wishes 120, comment 600) and this function keeps a
 *              last-resort ceiling with a visible «…» so nothing is lost
 *              silently.
 * @rule        findMessengerField is the READER of the messenger field and
 *              stays next to its writer: /api/lead's Telegram card parses the
 *              field back to build a one-tap chat link.
 */

/** Hard ceiling mirrored from /api/lead, which truncates at 1000. */
const MAX_MESSAGE_LENGTH = 1000;

/** Field order in the composed line. Fixed: the manager reads it daily. */
const FIELD_ORDER = ["body", "origin", "budget", "wishes", "messenger"] as const;

const FIELD_LABELS: Record<(typeof FIELD_ORDER)[number], string> = {
  body: "Кузов",
  origin: "Откуда",
  budget: "Бюджет",
  wishes: "Пожелания",
  messenger: "Мессенджер",
};

export interface LeadMessageParts {
  /** «Кузов» chip — Кроссовер / Седан / Минивэн / 7 мест / Другое. */
  body?: string | null;
  /** «Откуда» chip — Не важно — где выгоднее / Китай / Корея / Япония. */
  origin?: string | null;
  /** «Бюджет» chip — variant Б only, always optional. */
  budget?: string | null;
  /** Free single line: model, drive, year… */
  wishes?: string | null;
  /**
   * «Где удобнее переписываться?» chip — WhatsApp / Telegram / Max / WeChat
   * (MessengerPicker). Replaced variant Б's «Как связаться» row on 2026-10-14.
   */
  messenger?: string | null;
  /** The visitor's account in `messenger`; ignored without a messenger. */
  messengerLogin?: string | null;
  /** The visitor's own comment (variant А textarea). */
  comment?: string | null;
  /** Context supplied by the surface when the visitor typed nothing. */
  fallbackComment?: string | null;
  /**
   * Ready line such as «Вариант формы: Б». Passed by the caller only when it
   * should be printed (non-A variant, or any new field filled) — this
   * function does not decide what the test is.
   */
  variantLine?: string | null;
}

function cleaned(value: string | null | undefined): string {
  return typeof value === "string" ? value.trim() : "";
}

/**
 * Build the lead `message`.
 *
 * @input  parts — every field optional; empty and whitespace-only values are
 *         treated as absent
 * @output the message, or `undefined` when there is nothing to send
 * @important Returning `undefined` (not `""`) is deliberate: /api/lead and
 *            leadPersistence both treat an absent message as "no comment",
 *            which is what variant А has always sent.
 *
 * @example
 *   composeLeadMessage({ body: "Кроссовер", wishes: "7 мест", variantLine: "Вариант формы: Б" })
 *   // → "Кузов: Кроссовер; Пожелания: 7 мест\nВариант формы: Б"
 */
export function composeLeadMessage(parts: LeadMessageParts): string | undefined {
  // «Мессенджер: Telegram @ivan_petrov» — the account rides on the same field
  // so the manager reads where AND whom to write in one place.
  const messenger = cleaned(parts.messenger);
  const values: Record<(typeof FIELD_ORDER)[number], string> = {
    body: cleaned(parts.body),
    origin: cleaned(parts.origin),
    budget: cleaned(parts.budget),
    wishes: cleaned(parts.wishes),
    messenger: messenger ? [messenger, cleaned(parts.messengerLogin)].filter(Boolean).join(" ") : "",
  };

  const fields = FIELD_ORDER.map((key) => {
    const value = values[key];
    return value ? `${FIELD_LABELS[key]}: ${value}` : "";
  })
    .filter(Boolean)
    .join("; ");

  const comment = cleaned(parts.comment) || cleaned(parts.fallbackComment);
  const variantLine = cleaned(parts.variantLine);

  const blocks = [fields, comment, variantLine].filter(Boolean);
  if (blocks.length === 0) return undefined;

  const message = blocks.join("\n");
  if (message.length <= MAX_MESSAGE_LENGTH) return message;
  return `${message.slice(0, MAX_MESSAGE_LENGTH - 1)}…`;
}

/** Where the messenger field sits inside a composed message. */
export interface MessengerFieldMatch {
  /** Index of the «Мессенджер:» label in the message. */
  start: number;
  /** Index right after the field's value (before «;», «\n» or the end). */
  end: number;
  messenger: string;
  /** The visitor's account; empty when they left it blank. */
  login: string;
}

/**
 * Find the messenger field composeLeadMessage wrote, so the Telegram card can
 * turn it into a link. Lives HERE because this module owns the format: the
 * writer and the reader of «Мессенджер: Telegram @login» change together.
 *
 * @input  message — the lead `message` as /api/lead received it
 * @output the field's position and parts, or null when there is none
 * @important The value ends at «;» or a line break — the separators the
 *            composer uses. An account containing «;» is cut there; the
 *            card then links the part before it, nothing is lost from the
 *            text itself.
 */
export function findMessengerField(message: string): MessengerFieldMatch | null {
  const match = /Мессенджер: (WhatsApp|Telegram|Max|WeChat)(?: ([^;\n]*))?/.exec(message);
  if (!match) return null;
  return {
    start: match.index,
    end: match.index + match[0].length,
    messenger: match[1],
    login: (match[2] ?? "").trim(),
  };
}
